import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Footer from "@/components/landing/Footer";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import Seo from "@/components/Seo";

const Section = ({ number, title, children }) => (
  <section className="border-b border-[hsl(var(--border))] py-10 last:border-none">
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="lg:col-span-3">
        <p className="font-display text-3xl text-[hsl(var(--sand-dark))]">
          {number}
        </p>
        <h2 className="mt-1 font-display text-2xl text-[hsl(var(--ink))]">
          {title}
        </h2>
      </div>
      <div className="prose prose-neutral max-w-none text-[hsl(var(--ink-soft))] lg:col-span-9">
        {children}
      </div>
    </div>
  </section>
);

const List = ({ items }) => (
  <ul className="mt-3 list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--sand-dark))]">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

export const Terms = () => {
  return (
    <main data-testid="terms-page" className="bg-background">
      <Seo
        title="Algemene voorwaarden | EquiSight"
        description="De algemene voorwaarden voor het gebruik van EquiSight — accounts, abonnementen, aansprakelijkheid en intellectueel eigendom."
        path="/voorwaarden"
        breadcrumbs={[{ name: "Algemene voorwaarden", path: "/voorwaarden" }]}
      />
      <AnnouncementBar />
      <Header />
      <Breadcrumbs items={[{ name: "Algemene voorwaarden", path: "/voorwaarden" }]} />

      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
            Juridisch
          </p>
          <h1 className="mt-4 font-display text-5xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-6xl">
            Algemene Voorwaarden
          </h1>
          <p className="mt-6 text-[hsl(var(--ink-soft))]">
            Laatst bijgewerkt: 11-05-2026
          </p>
          <p className="mt-4 max-w-3xl text-[hsl(var(--ink-soft))]">
            Welkom bij EquiSight. Door gebruik te maken van onze applicatie
            ("de App") ga je akkoord met deze algemene voorwaarden. Lees deze
            zorgvuldig door.
          </p>
          <p className="mt-6">
            <Link
              to="/"
              className="text-sm text-[hsl(var(--sand-dark))] underline-offset-4 hover:underline"
            >
              ← Terug naar home
            </Link>
          </p>
        </div>
      </section>

      <article className="bg-background pb-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Section number="1" title="Definities">
            <List items={[
              "EquiSight: de aanbieder van de App en bijbehorende diensten.",
              "Gebruiker: iedere natuurlijke persoon of rechtspersoon die gebruikmaakt van de App.",
              "App: de mobiele en/of webapplicatie EquiSight inclusief alle functionaliteiten, modules en diensten.",
              "Content: alle gegevens, foto's, video's, rapporten en andere informatie die door de gebruiker wordt ingevoerd of gegenereerd.",
            ]} />
          </Section>

          <Section number="2" title="Toepasselijkheid">
            <p>
              Deze algemene voorwaarden zijn van toepassing op elk gebruik van
              de App en alle diensten van EquiSight.
            </p>
            <p className="mt-4">
              Door registratie en/of gebruik van de App accepteert de gebruiker
              deze voorwaarden.
            </p>
          </Section>

          <Section number="3" title="Gebruik van de App">
            <p>
              De App is bedoeld voor het beheren, analyseren en registreren van
              gegevens met betrekking tot paarden, training, gezondheid en
              prestaties.
            </p>
            <p className="mt-4">De gebruiker verklaart dat:</p>
            <List items={[
              "De ingevoerde gegevens juist en volledig zijn",
              "De App niet wordt gebruikt voor onrechtmatige doeleinden",
              "Geen inbreuk wordt gemaakt op rechten van derden",
            ]} />
            <p className="mt-4">
              EquiSight behoudt zich het recht voor accounts te blokkeren bij
              misbruik.
            </p>
          </Section>

          <Section number="4" title="Account en Toegang">
            <List items={[
              "De gebruiker is verantwoordelijk voor het geheimhouden van inloggegevens",
              "De gebruiker is volledig verantwoordelijk voor alle activiteiten binnen zijn/haar account",
              "EquiSight mag accounts opschorten of verwijderen bij overtreding van deze voorwaarden",
            ]} />
          </Section>

          <Section number="5" title="Abonnementen en Betaling">
            <p>EquiSight kan werken met:</p>
            <List items={[
              "Gratis functionaliteiten (basisgebruik)",
              "Betaalde abonnementen (premium)",
              "Betaalde add-on modules",
            ]} />
            <p className="mt-4">
              Betaling verloopt via de App Store, Google Play of andere
              betaalmethodes. Alle prijzen zijn inclusief btw, tenzij anders
              vermeld.
            </p>
            <p className="mt-4">
              EquiSight behoudt zich het recht voor prijzen en functionaliteiten
              te wijzigen.
            </p>
          </Section>

          <Section number="6" title="Functionaliteiten en Beschikbaarheid">
            <p>
              EquiSight streeft naar een goed werkende App, maar garandeert niet
              dat:
            </p>
            <List items={[
              "De App altijd foutloos of zonder onderbrekingen werkt",
              "Alle functionaliteiten altijd beschikbaar zijn",
            ]} />
            <p className="mt-4">Onderhoud, updates en storingen kunnen voorkomen.</p>
          </Section>

          <Section number="7" title="Geen Medisch of Veterinair Advies">
            <p>
              De App kan inzichten, analyses of aanbevelingen geven met
              betrekking tot:
            </p>
            <List items={["Gezondheid", "Training", "Gedrag"]} />
            <p className="mt-4">Deze informatie:</p>
            <List items={[
              "Is uitsluitend informatief",
              "Vervangt geen professioneel advies van een dierenarts, hoefsmid, trainer of specialist",
            ]} />
            <p className="mt-4 font-medium text-[hsl(var(--ink))]">
              Gebruik van deze informatie is volledig op eigen risico.
            </p>
          </Section>

          <Section number="8" title="Aansprakelijkheid">
            <p>EquiSight is niet aansprakelijk voor:</p>
            <List items={[
              "Schade aan paarden, personen of eigendommen",
              "Verkeerde interpretatie van data of analyses",
              "Onjuiste of onvolledige ingevoerde gegevens",
              "Beslissingen die worden genomen op basis van de App",
              "Tijdelijke onbeschikbaarheid van de App",
              "Verlies van data (ondanks eventuele back-up functionaliteiten)",
            ]} />
            <p className="mt-4">
              De totale aansprakelijkheid van EquiSight is in alle gevallen
              beperkt tot het bedrag dat de gebruiker in de laatste 12 maanden
              heeft betaald voor de App.
            </p>
          </Section>

          <Section number="9" title="Data en Content">
            <p>De gebruiker blijft eigenaar van alle ingevoerde data.</p>
            <p className="mt-4">Door gebruik van de App:</p>
            <List items={[
              "Verleent de gebruiker EquiSight een licentie om data te verwerken voor functionaliteit en verbetering van de App",
              "Mag EquiSight geanonimiseerde data gebruiken voor analyses en productontwikkeling",
            ]} />
            <p className="mt-4">De gebruiker is verantwoordelijk voor:</p>
            <List items={[
              "De juistheid van de data",
              "Het recht om deze data te gebruiken en te delen",
            ]} />
          </Section>

          <Section number="10" title="Privacy">
            <p>
              Persoonsgegevens worden verwerkt volgens de privacyverklaring van
              EquiSight:
            </p>
            <p className="mt-3">
              👉{" "}
              <Link
                to="/privacy"
                className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
              >
                www.equisight.nl/privacy
              </Link>
            </p>
          </Section>

          <Section number="11" title="Intellectueel Eigendom">
            <p>Alle rechten met betrekking tot:</p>
            <List items={[
              "Software",
              "Design",
              "Functionaliteiten",
              "Content van EquiSight",
            ]} />
            <p className="mt-4">blijven eigendom van EquiSight.</p>
            <p className="mt-4">Het is niet toegestaan om:</p>
            <List items={[
              "De App te kopiëren",
              "Reverse engineering toe te passen",
              "Onderdelen commercieel te exploiteren zonder toestemming",
            ]} />
          </Section>

          <Section number="12" title="Wijzigingen">
            <p>EquiSight mag deze voorwaarden op elk moment wijzigen.</p>
            <p className="mt-4">
              Bij belangrijke wijzigingen wordt de gebruiker geïnformeerd via:
            </p>
            <List items={["De App", "E-mail", "Website"]} />
            <p className="mt-4">
              Voortgezet gebruik betekent acceptatie van de nieuwe voorwaarden.
            </p>
          </Section>

          <Section number="13" title="Beëindiging">
            <p>De gebruiker kan het account op elk moment beëindigen.</p>
            <p className="mt-4">EquiSight kan accounts beëindigen of opschorten bij:</p>
            <List items={[
              "Overtreding van voorwaarden",
              "Misbruik",
              "Illegale activiteiten",
            ]} />
          </Section>

          <Section number="14" title="Toepasselijk Recht">
            <p>Op deze voorwaarden is Nederlands recht van toepassing.</p>
            <p className="mt-4">
              Geschillen worden voorgelegd aan de bevoegde rechter in Nederland.
            </p>
          </Section>

          <Section number="15" title="Contact">
            <p>Voor vragen over deze voorwaarden:</p>
            <p className="mt-4 text-[hsl(var(--ink))]">
              <strong>EquiSight</strong>
              <br />
              Website:{" "}
              <a
                href="https://www.equisight.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
              >
                www.equisight.nl
              </a>
              <br />
              E-mail:{" "}
              <a
                href="mailto:support@equisight.nl"
                className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
              >
                support@equisight.nl
              </a>
            </p>
          </Section>
        </div>
      </article>

      <Footer />
    </main>
  );
};

export default Terms;
